import { motion } from "framer-motion";
import { IMAGES, QUICK_BENEFITS } from "../../data/product";

const REVEAL = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};
const reveal = (delay = 0) => ({ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] });
const scrollToShop = () => document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" });

const NumberedBenefit = ({ benefit, index }) => (
  <motion.div
    {...REVEAL}
    transition={reveal(0.1 + index * 0.12)}
    className="flex flex-col items-center text-center"
    data-testid={`benefit-card-${index}`}
  >
    <div className="relative">
      <div className="w-44 h-44 sm:w-48 sm:h-48 lg:w-[230px] lg:h-[230px] rounded-full overflow-hidden border-2 border-[#FFFDF9] shadow-[0_10px_30px_rgba(58,42,37,0.15)]">
        <img
          src={benefit.image}
          alt={`CELUSTA benefit — ${benefit.title}`}
          loading="lazy"
          className="w-full h-full object-cover"
          data-testid={`benefit-image-${index}`}
        />
      </div>
      <span
        className="absolute -bottom-3 -left-2 font-serif-display italic text-6xl leading-none text-[#3A2A25] drop-shadow-[0_2px_6px_rgba(248,244,236,0.9)]"
        aria-hidden
        data-testid={`benefit-number-${index}`}
      >
        {index + 1}
      </span>
    </div>
    <h3 className="mt-7 font-serif-display text-xl text-[#2B1F1B]">{benefit.title}</h3>
    <p className="mt-2 text-sm font-light leading-relaxed text-[#756A64] max-w-[220px]">{benefit.text}</p>
  </motion.div>
);

export const Benefits = () => (
  <section className="bg-[#EDE3D6]" data-testid="benefits-section">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-8">
          <motion.div {...REVEAL} transition={reveal(0)} className="text-center lg:text-left">
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#C9A45C] mb-4">
              CELUSTA Leave-In Conditioner — 200 ml
            </p>
            <h2 className="font-serif-display font-medium text-4xl sm:text-5xl tracking-tight text-[#2B1F1B] leading-tight" data-testid="benefits-heading">
              Everyday care, in <span className="italic text-[#3A2A25]">three simple benefits.</span>
            </h2>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-6">
            {QUICK_BENEFITS.map((b, i) => (
              <NumberedBenefit key={b.title} benefit={b} index={i} />
            ))}
          </div>
        </div>

        <motion.div {...REVEAL} transition={reveal(0.2)} className="lg:col-span-4">
          <div className="relative rounded-2xl bg-[#F8F4EC] border border-[rgba(58,42,37,0.08)] p-8 flex flex-col items-center">
            <img
              src={IMAGES.packshot}
              alt="CELUSTA Leave-In Conditioner Spray 200 ml"
              loading="lazy"
              className="w-full max-w-[240px] h-auto object-contain"
              data-testid="benefits-product-image"
            />
            <button
              type="button"
              onClick={scrollToShop}
              className="mt-6 px-8 py-3.5 rounded-full bg-[#3A2A25] text-white text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#55423A] hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(58,42,37,0.25)] transition-[background-color,transform,box-shadow] duration-300"
              data-testid="benefits-shop-button"
            >
              Shop the Leave-In
            </button>
          </div>
        </motion.div>
      </div>

      <div className="mt-12 lg:mt-16 grid md:grid-cols-2 gap-6 lg:gap-10 items-stretch" data-testid="benefits-visual-row">
        <motion.div
          {...REVEAL}
          transition={reveal(0.1)}
          className="rounded-2xl bg-[#F8F4EC] border border-[rgba(58,42,37,0.08)] p-8 sm:p-12 flex flex-col items-center justify-center text-center"
          data-testid="benefits-visual-product"
        >
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#C9A45C]">Everyday care, made simple</p>
          <img
            src={IMAGES.heroBottle}
            alt="CELUSTA Leave-In Conditioner Spray bottle, 200 ml"
            loading="lazy"
            className="mt-8 w-full max-w-[260px] h-auto object-contain drop-shadow-[0_18px_35px_rgba(58,42,37,0.18)]"
            data-testid="benefits-visual-bottle-image"
          />
          <p className="mt-8 text-sm font-light leading-relaxed text-[#756A64] max-w-xs">
            A fine, lightweight leave-in mist — simply spray in and go. No rinsing required.
          </p>
        </motion.div>

        <motion.div
          {...REVEAL}
          transition={reveal(0.2)}
          className="relative rounded-2xl overflow-hidden min-h-[380px] md:min-h-0"
          data-testid="benefits-visual-hair"
        >
          <img
            src={IMAGES.whyLifestyle}
            alt="Soft, smooth, natural-looking hair with CELUSTA"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover object-top"
            data-testid="benefits-visual-hair-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#241813]/70 via-transparent to-transparent" aria-hidden />
          <p className="absolute bottom-5 left-5 px-5 py-2 rounded-full bg-white/85 backdrop-blur border border-[#3A2A25]/25 text-[11px] font-mono-accent uppercase tracking-[0.15em] text-[#3A2A25]">
            Soft. Smooth. Natural-looking shine.
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);
